import { useEffect, useState } from "react";
import { StyledDataTable } from "../../../../components/molecules/StyledDataTable";
import { CallSchedule, Playlist } from "../../../../types/api";
import {
  Loader,
  HeadSection,
  RightHeaderDiv,
  RightStyledH1,
  InactiveModal,
  ModalContainer,
} from "../../styles";
import { AddScheduleForm } from "./addScheduleForm";
import {
  CALL_SCHEDULE_TABLE_COLUMNS,
  DELETED_CALL_SCHEDULE_TABLE_COLUMNS,
} from "./columns";
import { EditScheduleModal } from "./editScheduleModal";
import { useMutation, useQuery } from "@apollo/client";
import { DeletedCallSchedulesResponse, DeleteScheduleInputs } from "./types";
import {
  GET_DELETED_CALL_SCHEDULES,
  RESTORE_CALL_SCHEDULE,
} from "../../queries";
import {
  Alert,
  Button,
  CircularProgress,
  Modal,
  Snackbar,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { WebLinkButton } from "../../../../components/atoms/WebLinkButton";
import { ButtonContainer } from "components/molecules/Modal/styles";
// import { Modal } from "components/molecules/Modal";
import { UserDisplayButton } from "styles/flex";
import { PushCallDialog, PushMaintenanceCallDialog } from "./PushCallModal";
import { ConfirmDialog } from "components/molecules/ConfirmDialog";
import axios from "axios";
import { InactivePlaylist } from "./InactivePlaylistCheck";
import { CallPushedModal } from "./CallPushedNote";
import { MONETA_API_BASE_URL } from "config";

interface CallScheduleProps {
  callSchedules: CallSchedule[];
  userId: number;
  playlists?: Playlist[];
  inMaintenance?: boolean;
}

export const CallSchedules: React.FC<CallScheduleProps> = (props) => {
  const [showError, setShowError] = useState<boolean>(false);
  const [editScheduleModal, setEditScheduleModal] = useState<boolean>(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState<boolean>(false);
  const [callPushModal, setcallPushModal] = useState<boolean>(false);

  const [selectedSchedule, setSelectedSchedule] = useState<
    CallSchedule | undefined
  >();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const handleRowClicked = (row: CallSchedule) => {
    setSelectedSchedule(row);
    setEditScheduleModal(true);
  };

  const {
    data,
    error: getDeletedCallScheduleError,
    loading: getDeleteCallScheduleLoading,
  } = useQuery<DeletedCallSchedulesResponse>(GET_DELETED_CALL_SCHEDULES, {
    variables: { userId: props.userId },
    fetchPolicy: "no-cache",
  });
  const deletedCallSchedules = data?.deletedCallSchedules ?? [];

  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(getDeleteCallScheduleLoading);
  }, [getDeleteCallScheduleLoading]);

  const [restoreCallSchedule, { error }] = useMutation<DeleteScheduleInputs>(
    RESTORE_CALL_SCHEDULE
  );
  const onClickRestore: React.MouseEventHandler<HTMLButtonElement> = async (
    state
  ) => {
    try {
      const button: HTMLButtonElement = state.currentTarget;
      await restoreCallSchedule({
        variables: {
          id: parseInt(button.id),
        },
      });
      navigate("/refresh");
    } catch (error) {
      console.error("Failed to restore call schedule");
      setShowError(true);
    }
  };

  useEffect(() => {
    if (error || getDeletedCallScheduleError) {
      setShowError(true);
    }
  }, [error, getDeletedCallScheduleError]);

  const SinglePushCall = () => {
    axios.post(
      `${MONETA_API_BASE_URL}/call-flow/v2/start`,
      {
        callScheduleId: null,
        accountId: props.userId,
      },
      {
        withCredentials: true,
      }
    );
    setConfirmSubmitOpen(false);
    setcallPushModal(true);
  };

  const ActivePlaylist = props.playlists?.find((item: any) => item?.isActive);

  return (
    <>
      {isLoading ? (
        <Loader>
          <CircularProgress />
        </Loader>
      ) : (
        <div style={{ width: "100%" }}>
          <HeadSection>
            <RightHeaderDiv>
              <RightStyledH1>Call Schedules</RightStyledH1>
            </RightHeaderDiv>
            <UserDisplayButton
              onClick={() => {
                setConfirmSubmitOpen(true);
              }}
            >
              <Button>Push Call Now</Button>
            </UserDisplayButton>
          </HeadSection>
          <ButtonContainer>
            <WebLinkButton
              href={
                "https://docs.google.com/document/d/1y0AEfWmmlXsfntzK5aE6xeGpZd_oYQSXDZ4cJ97LzGM/edit"
              }
              text={"Open eS Cheat Sheet"}
            />
            <WebLinkButton
              href={"https://crontab.guru/"}
              text={"Open CronTab Guru"}
            />
          </ButtonContainer>
          <br />
          <StyledDataTable
            columns={CALL_SCHEDULE_TABLE_COLUMNS}
            data={props.callSchedules}
            onRowClicked={handleRowClicked}
          />
          <br />
          <AddScheduleForm />
          <br />
          <div style={{ width: "100%" }}>
            <RightHeaderDiv>
              <RightStyledH1>Deleted Call Schedules</RightStyledH1>
            </RightHeaderDiv>
          </div>
          <StyledDataTable
            columns={DELETED_CALL_SCHEDULE_TABLE_COLUMNS(onClickRestore)}
            data={deletedCallSchedules}
          />
          <EditScheduleModal
            trigger={editScheduleModal}
            setTrigger={setEditScheduleModal}
            callSchedule={selectedSchedule as CallSchedule}
          />
          {ActivePlaylist || props.inMaintenance ? (
            <ConfirmDialog
              confirmOpen={confirmSubmitOpen}
              dialogContent={
                props.inMaintenance ? PushMaintenanceCallDialog : PushCallDialog
              }
              onSubmit={SinglePushCall}
              onClose={() => setConfirmSubmitOpen(false)}
            />
          ) : (
            <Modal
              title=""
              children={
                <InactivePlaylist onClose={() => setConfirmSubmitOpen(false)} />
              }
              open={confirmSubmitOpen}
              onClose={() => setConfirmSubmitOpen(false)}
            />
          )}
          <Modal
            title=""
            children={
              <CallPushedModal onClose={() => setcallPushModal(false)} />
            }
            open={callPushModal}
            onClose={() => setcallPushModal(false)}
          />
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={showError}
            onClose={() => setShowError(false)}
          >
            <Alert onClose={() => setShowError(false)} severity="error">
              Failed to restore call schedule.
              {getDeletedCallScheduleError &&
                "Failed to get deleted call schedule."}
            </Alert>
          </Snackbar>
        </div>
      )}
    </>
  );
};
